/**
 * Centralized timeout constants for database transactions, AI calls, and polling.
 *
 * Categories:
 * - INNGEST_TX: Fast timeouts for Inngest jobs (Inngest handles retries)
 * - INNGEST_AI: AI request timeouts for Inngest/background jobs
 * - TRPC_TX: Medium timeouts for tRPC CRUD operations
 * - AI_DIRECT: HTTP timeout for AI API requests (bypassing Inngest)
 * - POLLING: Client-side polling intervals, limits, and real-time sync settings
 * - SAVE: Client-side save retry settings
 */

// ─────────────────────────────────────────────────────────────────────────────
// Inngest Job Transaction Timeouts
// Interactive transactions over the pgBouncer transaction-mode pooler need
// headroom: each query is a serial round-trip and the pooler recycles the
// connection (→ Prisma "Transaction not found") if the transaction outlives its
// budget. These stay modest because Inngest retries failed jobs automatically,
// but must comfortably cover a few serial round-trips on a slow remote DB.
// Prefer hoisting reads out of write transactions (withServiceRoleReads) over
// widening these further.
// ─────────────────────────────────────────────────────────────────────────────
export const INNGEST_TX = {
  /** Light reads: fetching single docs, simple queries */
  READ_LIGHT_MS: { maxWait: 2_000, timeout: 5_000 },
  /** Heavy reads: fetching multiple docs, context assembly with dependencies */
  READ_HEAVY_MS: { maxWait: 2_000, timeout: 8_000 },
  /** Writes: creating blocks, versions, emitting events */
  WRITE_MS: { maxWait: 2_500, timeout: 12_000 },
  /**
   * One venture firm's derive upsert: firm + funds + every partner + every seat
   * in a single transaction. A large firm (dozens of partners, hundreds of
   * seats) blows past the 12s write budget even after round-trips are batched,
   * so give this its own headroom. Still one tx per firm for atomicity.
   */
  VENTURE_DERIVE_FIRM_MS: { maxWait: 5_000, timeout: 60_000 },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// tRPC Transaction Timeouts
// Medium timeouts for standard CRUD operations.
// ─────────────────────────────────────────────────────────────────────────────
export const TRPC_TX = {
  /** Standard read operations */
  READ_MS: { maxWait: 5_000, timeout: 10_000 },
  /** Standard write operations */
  WRITE_MS: { maxWait: 10_000, timeout: 20_000 },
  /**
   * Orbit Scan write-back — ONE transaction per scanned person. A single VC
   * partner can carry 8–15 roles, and each role writes an Orbit Company +
   * affiliation (plus firmographics and feed rows). Even after the per-job
   * lookups are preloaded, that serial chain of writes outruns the 20s
   * WRITE_MS budget on the pgBouncer transaction-mode pooler; once the budget
   * lapses the pooler recycles the connection and every later query throws
   * P2028 "Transaction not found", which the retry wrapper re-runs forever and
   * the scan 500s. One tx per person keeps the batch bounded; this budget
   * covers the writes a long career still needs.
   */
  SCAN_WRITEBACK_MS: { maxWait: 5_000, timeout: 30_000 },
  /**
   * One vg_research_upsert call (LEV-2664): keyed reads, then one createMany
   * per table and one compare-and-set updateMany per changed row, at most
   * 200 rows and 1,000 round participants. Runs with maxRetries: 1, since
   * the caller re-runs the whole call once on a lost compare-and-set.
   */
  VENTURE_RESEARCH_MS: { maxWait: 5_000, timeout: 20_000 },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Inngest / Background AI Call Timeouts
// ─────────────────────────────────────────────────────────────────────────────
export const INNGEST_AI = {
  /** Default HTTP timeout for Inngest background AI calls. */
  REQUEST_TIMEOUT_MS: 120_000,
  /** Extended timeout for any web-search-enabled AI call. */
  WEB_SEARCH_REQUEST_TIMEOUT_MS: 240_000,
  /** Extended timeout for project evaluation (flagship model + medium reasoning + structured output). */
  PROJECT_EVALUATION_TIMEOUT_MS: 240_000,
  /**
   * Extended timeout for structuring the investor prospects list (no web
   * search, but a 30-firm shortlist's output outgrew the 120s default —
   * measured at 20 firms against the default, this pass's output scales with
   * the shortlist size).
   */
  PROSPECTS_STRUCTURE_TIMEOUT_MS: 240_000,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Direct AI Call Timeouts (bypassing Inngest)
// Must be long enough for LLM completion but under Vercel's runtime limits.
// ─────────────────────────────────────────────────────────────────────────────
export const AI_DIRECT = {
  /** HTTP timeout for direct AI API requests (e.g. synchronous route handlers). */
  REQUEST_TIMEOUT_MS: 45_000,
  /**
   * Brutal mode (accelerators.applications.runBrutalMode): one medium-effort
   * call that scores every answer and writes interview questions too, which
   * outgrew the 120s default. MUST stay below the tRPC route's maxDuration
   * (300s, src/app/api/trpc/[trpc]/route.ts) with room for the save after it.
   */
  ACCELERATOR_BRUTAL_TIMEOUT_MS: 240_000,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Postgres statement_timeout (src/lib/dbStatementTimeout.ts)
// A per-statement cap set INSIDE an interactive transaction via set_config,
// distinct from the tx-level maxWait/timeout above.
// ─────────────────────────────────────────────────────────────────────────────
export const DB_STATEMENT_TIMEOUT = {
  /**
   * LEV-2002: the warm-path recompute query ran 13–43s in production, well
   * past withRls's transaction timeout — Prisma's P2028 retry then re-issued
   * the whole transaction while Postgres kept executing the abandoned one,
   * tripling load. MUST stay below every transaction timeout that wraps it,
   * so Postgres cancels a slow statement (57014) before P2028 can fire and
   * trigger a retry that just re-issues it. This bounds EACH statement, not
   * the transaction: several statements near the cap can still add up past
   * the transaction timeout, which then stays the backstop for the sum.
   */
  WARM_PATHS_MS: 3_000,
  /**
   * Each statement of the eval-task reconcile transaction
   * (reconcileEvalImprovementTasksForProject). MUST stay below
   * INNGEST_TX.WRITE_MS.timeout, the transaction budget it runs under.
   */
  EVAL_TASK_RECONCILE_MS: 5_000,
  /**
   * Each read of the task-kanban scheduled-start scan (pending dated Actions
   * across every project, `Action(status, scheduledFor)` index). Each read
   * is the only statement in its INNGEST_TX.READ_HEAVY_MS transaction, so
   * this MUST stay below that timeout; a second statement in the same
   * transaction would need it below half.
   */
  SCHEDULED_START_SCAN_MS: 5_000,
  /**
   * Each read of the weekly overdue-snapshot scan (active dated Actions up
   * to a Thursday across every project, `Action(status, scheduledFor)`
   * index). Each read is the only statement in its INNGEST_TX.READ_HEAVY_MS
   * transaction, so this MUST stay below that timeout; a second statement in
   * the same transaction would need it below half.
   */
  TASK_OVERDUE_SNAPSHOT_SCAN_MS: 5_000,
  /**
   * Each statement of loadEvalAreaInputsTx, the eval-area inputs read inside
   * the founder-facing evaluation's withRls transaction. MUST stay below the
   * withRls transaction timeout, so Postgres cancels a slow scan (57014, not
   * retried) before P2028 re-issues the whole transaction.
   */
  EVAL_AREA_INPUTS_MS: 3_000,
  /**
   * LEV-2016: the project-purge cascade (eight `deleteMany`s plus the row
   * delete) inside one `withServiceRole` transaction. A big project's
   * `StructuralEvent` or `Block` delete is the slow one. Stays below
   * `INNGEST_TX.WRITE_MS.timeout` so Postgres cancels the slow statement
   * (57014, not retried) before the transaction budget lapses and
   * `runPrismaTransactionWithRetry` re-issues the whole cascade on top of the
   * deletes Postgres is still running.
   */
  PROJECT_PURGE_CASCADE_MS: 8_000,
  /**
   * LEV-2620: each statement of one bulk-decline chunk (bulkDecline: up to
   * 100 compare-and-set updates and one correction insert in a single
   * withServiceRole) or bulk-reopen chunk (bulkReopen: one open-card read, up
   * to 100 compare-and-set updates and one correction delete). MUST stay
   * below every transaction timeout the chunk can run under, so Postgres
   * cancels a statement stuck on a row lock (57014) before the transaction
   * times out (P2028) and leaves it running. Those timeouts are
   * TRPC_TX.WRITE_MS.timeout capped to the call's remaining deadline, never
   * below this plus a second (bulkTxOptions in bulkRunBudget.ts).
   *
   * Also bounds each batch of bulkDecline's lastFiledBefore filing read
   * (one withServiceRole per BULK_DECLINE_FILING_TOKEN_BATCH prefix words),
   * under the same floor with TRPC_TX.READ_MS.timeout as the cap; a
   * cancelled read reaches the caller as InvalidBulkFilter.
   */
  VENTURE_BULK_DECIDE_MS: 5_000,
  /**
   * The graduation / stage-compass-recompute advisory-lock critical section
   * (`advanceStageIfComplete` + `StageCompassService` persist). Both writers take
   * `pg_advisory_xact_lock(hashtext(projectId))` so they serialize instead of
   * deadlocking; this bounds how long one waits on the lock before Postgres
   * cancels the statement (57014, not retried) rather than a P2028 re-queueing
   * it behind the same lock. Stays below the withRls transaction timeout.
   */
  STAGE_ADVANCE_LOCK_MS: 5_000,
  /**
   * Each statement of a vg_research_upsert transaction (LEV-2664). MUST stay
   * below TRPC_TX.VENTURE_RESEARCH_MS.timeout, so Postgres cancels a slow
   * statement (57014, not retried) before P2028 re-issues the whole call.
   */
  VENTURE_RESEARCH_MS: 5_000,
  /**
   * Each statement of the task-kanban board reads: `actions.getKanban` with
   * the owner's flag ON (one week) and `actions.getKanbanAll` (By Dimension,
   * whose completed-task read spans the project's whole history). Stays
   * below the withRls transaction timeout so Postgres cancels a slow read
   * (57014, not retried) before P2028 re-issues the board's transaction.
   */
  KANBAN_BOARD_MS: 3_000,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Client-Side Polling & Query Caching
// ─────────────────────────────────────────────────────────────────────────────
export const POLLING = {
  /** Default max wait for document appearance */
  DOCUMENT_WAIT_MS: 1_800_000, // 30 minutes
  /** Wait for first document in empty project */
  FIRST_DOCUMENT_WAIT_MS: 300_000, // 5 minutes
  /** Default polling interval */
  INTERVAL_MS: 400,
  /** Slow polling for long waits */
  INTERVAL_SLOW_MS: 5_000,
  /** Idle polling: no pending AI generation */
  IDLE_MS: 30_000,
  /** Active polling: AI generation running */
  ACTIVE_MS: 2_000,
  /**
   * Cap on holding the first-canvas welcome for the impact queue. Covers the
   * project-init compass job (two LLM calls) with margin; past it the welcome
   * goes out without a queue and must not name a next step.
   */
  FIRST_WELCOME_QUEUE_WAIT_MS: 45_000,
  /** Staleness threshold: warn user after this many seconds of active generation */
  GENERATION_STALE_S: 180,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Client-Side Save Settings
// ─────────────────────────────────────────────────────────────────────────────
export const SAVE = {
  /** Number of client-side retries before giving up */
  CLIENT_RETRY_COUNT: 1,
} as const;

/** Debounce for silent CSV saves (hide/show toggles) — 4s to batch rapid clicks */
export const CSV_SAVE_DEBOUNCE_MS = 4_000;
